import React from "react";
import styled from "styled-components";
import { scaleBand, scaleLinear } from "d3-scale";

import { colors } from "utils";
import {
  COLLAB_LEVELS,
  GROUPING,
  axisTicks,
  axisTop,
  chartTitle,
  levelFill,
} from "./trendPeriods";

// "Collaboration Trend By Week" — the design's own chart, node 2640:39237.
//
// ONE BLOCK IS ONE SESSION. There is no continuous quantity here: the y axis
// counts sessions, and a week of three sessions is three unit-high blocks
// stacked from the baseline in ascending level order. That is why the geometry
// below is stated in the design's own numbers rather than derived from the
// data — every measurement is read off the Figma node it is named after.

// Frame 2921:19075 "Bar graph": 658 x 336, of which the first 81px is the y
// axis label column and the bars occupy the 268px above the baseline.
const VIEW_W = 658;
const VIEW_H = 336;
const AXIS_W = 81;
const BASELINE = 268;

// Frame 2921:19088 "Empty": the headroom above the tallest bar. The plotted
// band is therefore 73..268, whatever the axis tops out at.
const HEADROOM = 73;

// Frame 2921:19087: `gap-[3px]` between blocks, `rounded-[4px]` on each, and
// `w-[82px]` per bar inside a 115.4px band.
const GAP = 3;
const BAR_W = 82;
const BAR_RADIUS = 4;

const HALF_PX = 0.5;

// The axis label column is 298px tall with 16px of padding at its foot, and
// centres "Session Count" in what is left: its middle is at y = 141.
const AXIS_TITLE_CENTRE = (298 - 16) / 2;

// The dashed gridlines stop 9px short of the y axis and of the right edge: a
// 559px run across the frame's five weeks.
const GRID_INSET = 9;

// The two-line week label under each bar: 8px below the baseline, Inter 16/22.
const LABEL_TOP = BASELINE + 8;
const LINE_H = 22;

// The narrowest a week's column may get: the frame's own 115.4px band, which is
// what a two-line "6/29/2026 -" / "7/5/2026" label needs to not run into its
// neighbour. Past five weeks the chart grows at this width and scrolls.
const MIN_BAND = 115.4;

// The scale's arithmetic lands on 206.00000000000003 where the design says 206.
// Rounded before it reaches the SVG so the markup carries the design's own
// numbers rather than their floating-point residue.
const px = (value) => Math.round(value * 100) / 100;

/**
 * The chart, or nothing at all when there is nothing to draw.
 *
 * `periods` arrive in CHRONOLOGICAL order and are drawn that way regardless
 * of how the table beneath it is sorted: a trend read right-to-left because
 * someone sorted a column by minutes is not a trend.
 */
export const CollaborationTrendChart = ({ periods: allPeriods = [], grouping = GROUPING.WEEK }) => {
  if (!allPeriods.length) return null;

  // Scored sessions only. The frame's key has four levels and nothing else, so
  // a session with no collaboration level is not drawn -- a grey block would
  // need a key entry the design does not have. The table beneath still counts
  // every session in its Session Count.
  const periods = allPeriods.map((period) => {
    const scored = period.sessions.filter(
      (session) => session.level !== null && session.level !== undefined
    );
    // The axis is scaled to what is drawn, so the tallest stack meets its line.
    return { ...period, sessions: scored, sessionCount: scored.length };
  });

  const top = axisTop(periods);

  // scaleBand distributes the periods across the plot; with the design's five it
  // returns the design's 115.4px band and the bar sits at its full 82px. With
  // more columns the band narrows and the bar narrows with it, down to a floor
  // the chart scrolls past rather than shrinking below.
  const band = scaleBand()
    .domain(periods.map((period) => period.key))
    .range([AXIS_W, Math.max(VIEW_W, AXIS_W + periods.length * MIN_BAND)]);

  const width = band.range()[1];
  const barWidth = px(Math.min(BAR_W, Math.max(24, band.bandwidth() - 32)));

  // The value axis: 0 sits on the baseline, `max` at the foot of the headroom.
  // One unit is therefore (268 - 73) / max, of which 3px is the gap between
  // blocks — which is exactly how the design's 62px block and 65px unit arise.
  const y = scaleLinear().domain([0, top]).range([BASELINE, HEADROOM]);
  const unit = (BASELINE - HEADROOM) / top;
  const blockHeight = px(Math.max(1, unit - GAP));

  // The nth block from the baseline, 1-indexed: its top edge is the gridline
  // for that value, dropped by the gap.
  const blockTop = (index) => px(y(index + 1) + GAP);

  const ticks = axisTicks(top);

  return (
    <Figure>
      <Title>{chartTitle(grouping)}</Title>

      <Plot>
        <Svg
          viewBox={`0 0 ${width} ${VIEW_H}`}
          $width={width}
          role="img"
          aria-label={`${chartTitle(grouping)}, ${periods.length} ${grouping}s`}
          data-testid="collaboration-trend-chart"
        >
          {/* The dashed rules the design draws as node 2921:19141, in the
              middle of the 3px gap above each level's block. Derived from the
              same scale as the blocks, so they cannot drift out of alignment
              with the bars the way a separately-computed overlay would. */}
          {ticks
            .filter((value) => value > 0)
            .map((value) => (
              <line
                key={`grid-${value}`}
                x1={AXIS_W + GRID_INSET}
                x2={width - GRID_INSET}
                y1={px(y(value) + GAP / 2)}
                y2={px(y(value) + GAP / 2)}
                stroke="#ACAFAD"
                strokeDasharray="2 2"
              />
            ))}

          {/* Both axes, `gray/950` on the design's frame borders: the 1px
              column just left of the plot and the 1px row just under the
              bars. Centred on the half pixel so each covers one whole pixel. */}
          <line x1={AXIS_W - HALF_PX} x2={AXIS_W - HALF_PX} y1={0} y2={BASELINE + 1} stroke="#030712" />
          <line x1={AXIS_W - 1} x2={width} y1={BASELINE + HALF_PX} y2={BASELINE + HALF_PX} stroke="#030712" />

          {ticks.map((value) => (
            <TickLabel
              key={`tick-${value}`}
              x={AXIS_W - 16}
              y={value === 0 ? BASELINE : px(y(value) + GAP)}
              textAnchor="end"
              dominantBaseline="middle"
            >
              {value}
            </TickLabel>
          ))}

          <AxisTitle
            transform={`translate(35, ${AXIS_TITLE_CENTRE}) rotate(-90)`}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            Session Count
          </AxisTitle>

          {periods.map((period) => {
            const left = px(band(period.key) + (band.bandwidth() - barWidth) / 2);
            const centre = px(band(period.key) + band.bandwidth() / 2);
            return (
              <g key={period.key} data-testid="trend-period">
                {period.sessions.map((session, index) => (
                  <rect
                    key={session.session_id}
                    data-testid="trend-block"
                    data-level={session.level ?? "unscored"}
                    x={left}
                    y={blockTop(index)}
                    width={barWidth}
                    height={blockHeight}
                    rx={BAR_RADIUS}
                    fill={levelFill(session.level)}
                  />
                ))}
                {/* A week needs both ends and so wraps to two lines; a month
                    is one word and must NOT be given an empty second line. */}
                <PeriodLabel x={centre} y={LABEL_TOP} textAnchor="middle">
                  <tspan x={centre} dy={LINE_H - 6}>
                    {period.labelParts.end
                      ? `${period.labelParts.start} -`
                      : period.labelParts.start}
                  </tspan>
                  {period.labelParts.end && (
                    <tspan x={centre} dy={LINE_H}>
                      {period.labelParts.end}
                    </tspan>
                  )}
                </PeriodLabel>
              </g>
            );
          })}
        </Svg>
      </Plot>

      <Key>
        <KeyTitle>Key:</KeyTitle>
        <KeyItems>
          {COLLAB_LEVELS.map((entry) => (
            <KeyItem key={entry.level}>
              <Swatch style={{ backgroundColor: entry.fill }} />
              <KeyText>
                {"= "}
                <strong>{`${entry.short}:`}</strong>
                {` ${entry.name}`}
              </KeyText>
            </KeyItem>
          ))}
        </KeyItems>
      </Key>
    </Figure>
  );
};

// On the page ground, not a card: the frame's Data Viz (4485:72769) has no
// fill and no inset -- title, graph and key 8px apart, straight on the body.
const Figure = styled.figure`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
`;

// Header/H1 in the design: ABC Marfa Semibold 24/32, centred.
const Title = styled.figcaption`
  margin: 0;
  text-align: center;
  font-family: "ABC Marfa", "Inter", sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: ${colors.nightlyWoods500};
`;

// The design centres a 658px graph in a 1072px body. Beyond five columns the
// chart grows past that and scrolls, rather than compressing bars until the
// stacks stop being readable. Centred with `margin: auto` on the SVG rather
// than `justify-content: center`, which would push an overflowing chart's
// first weeks off the left edge where the scrollbar cannot reach them.
const Plot = styled.div`
  display: flex;
  overflow-x: auto;
  /* Never vertically: see TableWrapper for why a fractional height would
     otherwise trap the page's scroll here. */
  overflow-y: hidden;
`;

// Always drawn at its own width: shrinking it to fit would shrink the axis
// text and the week labels with it until they overlap.
const Svg = styled.svg`
  flex: 0 0 auto;
  width: ${(props) => props.$width}px;
  /* Its own height in whole pixels, not \`auto\`: the ratio from the viewBox
     lands on 335.99px, and that fraction travels up into the page's height. */
  height: ${VIEW_H}px;
  margin: 0 auto;
`;

const AxisText = styled.text`
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.25px;
  fill: ${colors.nightlyWoods500};
`;

const TickLabel = styled(AxisText)``;
const AxisTitle = styled(AxisText)``;
const PeriodLabel = styled(AxisText)``;

// "Key:" on the items' line, centred in the 24px row as the frame sets it.
// The items may still wrap on a narrow screen.
const Key = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const KeyTitle = styled.p`
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: ${colors.nightlyWoods500};
`;

const KeyItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 32px;
  row-gap: 8px;
`;

const KeyItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Swatch = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
`;

const KeyText = styled.span`
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0px;
  color: ${colors.nightlyWoods500};

  strong {
    font-weight: 700;
  }
`;
